import Phaser from "phaser";

import { PREMIUM_SHOP_ART, TROPHY_HALL_ART } from "../config/artAssets";

export type RewardVaultVisualState = {
  hasRewards: boolean;
  ledgerCount: number;
  reducedMotion: boolean;
};

export type RewardVaultSceneCallbacks = {
  onReady?(): void;
};

type Facet = {
  body: Phaser.GameObjects.Polygon;
  line: Phaser.GameObjects.Polygon;
};

export class RewardVaultScene extends Phaser.Scene {
  readonly #callbacks: RewardVaultSceneCallbacks;
  readonly #renderScale: number;
  #visualState: RewardVaultVisualState;
  #layoutWidth = 0;
  #layoutHeight = 0;
  #background!: Phaser.GameObjects.Image;
  #shade!: Phaser.GameObjects.Rectangle;
  #vault!: Phaser.GameObjects.Container;
  #crown!: Phaser.GameObjects.Image;
  #plinth!: Phaser.GameObjects.Graphics;
  #facets: Facet[] = [];
  #rays: Phaser.GameObjects.Graphics[] = [];
  #tweens: Phaser.Tweens.Tween[] = [];

  constructor(
    callbacks: RewardVaultSceneCallbacks,
    visualState: RewardVaultVisualState,
    renderScale: number,
  ) {
    super({ key: "RewardVault" });
    this.#callbacks = callbacks;
    this.#visualState = visualState;
    this.#renderScale = renderScale;
  }

  preload(): void {
    this.load.image(PREMIUM_SHOP_ART.background.key, PREMIUM_SHOP_ART.background.url);
    this.load.image(TROPHY_HALL_ART.seasonCrown.key, TROPHY_HALL_ART.seasonCrown.url);
  }

  create(): void {
    this.#background = this.add.image(0, 0, PREMIUM_SHOP_ART.background.key).setDepth(0);
    this.#shade = this.add.rectangle(0, 0, 10, 10, 0x031f2b, 0.4).setOrigin(0).setDepth(1);

    this.#plinth = this.add.graphics();
    this.#plinth
      .fillStyle(0x052f3b, 0.9)
      .lineStyle(1.2, 0xd3a963, 0.72)
      .fillPoints([
        new Phaser.Geom.Point(-92, 53),
        new Phaser.Geom.Point(92, 53),
        new Phaser.Geom.Point(69, 84),
        new Phaser.Geom.Point(-69, 84),
      ], true)
      .strokePoints([
        new Phaser.Geom.Point(-92, 53),
        new Phaser.Geom.Point(92, 53),
        new Phaser.Geom.Point(69, 84),
        new Phaser.Geom.Point(-69, 84),
      ], true);

    this.#crown = this.add.image(0, -18, TROPHY_HALL_ART.seasonCrown.key);
    this.#crown.setScale(0.255);

    const facetPositions = [
      { x: -76, y: 35, scale: 0.86 },
      { x: -43, y: 57, scale: 0.68 },
      { x: 45, y: 57, scale: 0.68 },
      { x: 77, y: 35, scale: 0.86 },
    ];
    this.#facets = facetPositions.map(({ x, y, scale }, index) => {
      const points = [0, -12, 10, -2, 7, 12, -7, 12, -10, -2];
      const body = this.add
        .polygon(x, y, points, index % 2 === 0 ? 0x59e6dc : 0xf2c46f, 0.72)
        .setScale(scale)
        .setBlendMode(Phaser.BlendModes.ADD);
      const line = this.add
        .polygon(x, y, points, 0xffffff, 0)
        .setStrokeStyle(1, index % 2 === 0 ? 0xb8fff7 : 0xffe3ac, 0.9)
        .setScale(scale);
      return { body, line };
    });

    this.#rays = Array.from({ length: 7 }, (_, index) => {
      const ray = this.add.graphics();
      const angle = Phaser.Math.DegToRad(-72 + index * 24);
      ray.lineStyle(index === 3 ? 1.8 : 1, index % 2 === 0 ? 0x86fff3 : 0xffd58a, 0.32);
      ray.beginPath();
      ray.moveTo(Math.cos(angle) * 54, Math.sin(angle) * 40 - 8);
      ray.lineTo(Math.cos(angle) * 106, Math.sin(angle) * 78 - 8);
      ray.strokePath();
      return ray;
    });

    this.#vault = this.add.container(0, 0, [
      ...this.#rays,
      this.#plinth,
      this.#crown,
      ...this.#facets.flatMap(({ body, line }) => [body, line]),
    ]).setDepth(3);

    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
    this.#applyVisualState();
    this.#callbacks.onReady?.();
  }

  setVisualState(visualState: RewardVaultVisualState): void {
    if (
      this.#visualState.hasRewards === visualState.hasRewards &&
      this.#visualState.ledgerCount === visualState.ledgerCount &&
      this.#visualState.reducedMotion === visualState.reducedMotion
    ) return;
    this.#visualState = visualState;
    if (this.sys.isActive()) this.#applyVisualState();
  }

  debugSnapshot(): Record<string, unknown> {
    return {
      hasRewards: this.#visualState.hasRewards,
      ledgerCount: this.#visualState.ledgerCount,
      crownAlpha: Number(this.#crown.alpha.toFixed(2)),
      facetAlpha: this.#facets.map(({ body }) => Number(body.alpha.toFixed(2))),
      reducedMotion: this.#visualState.reducedMotion,
    };
  }

  #layout(size: Phaser.Structs.Size): void {
    const physicalWidth = Math.round(size.width);
    const physicalHeight = Math.round(size.height);
    const width = Math.round(physicalWidth / this.#renderScale);
    const height = Math.round(physicalHeight / this.#renderScale);
    if (width <= 0 || height <= 0 || (width === this.#layoutWidth && height === this.#layoutHeight)) return;
    this.#layoutWidth = width;
    this.#layoutHeight = height;
    this.cameras.main
      .setViewport(0, 0, physicalWidth, physicalHeight)
      .setZoom(this.#renderScale)
      .centerOn(width / 2, height / 2);
    const scale = Math.max(width / PREMIUM_SHOP_ART.background.width, height / PREMIUM_SHOP_ART.background.height);
    this.#background.setPosition(width / 2, height * 0.43).setScale(scale);
    this.#shade.setSize(width, height);
    this.#vault.setPosition(width / 2, Math.round(height * 0.62));
  }

  #applyVisualState(): void {
    this.#tweens.forEach((tween) => tween.stop());
    this.#tweens = [];
    this.tweens.killTweensOf(this.#vault);
    this.tweens.killTweensOf(this.#crown);
    this.#facets.forEach(({ body, line }) => {
      this.tweens.killTweensOf(body);
      this.tweens.killTweensOf(line);
    });
    this.#rays.forEach((ray) => this.tweens.killTweensOf(ray));

    const hasRewards = this.#visualState.hasRewards;
    this.#crown.clearTint().setAlpha(hasRewards ? 1 : 0.55);
    if (!hasRewards) this.#crown.setTint(0x78989a);
    this.#facets.forEach(({ body, line }, index) => {
      const active = hasRewards && index < Math.min(4, Math.max(1, this.#visualState.ledgerCount));
      body.setAlpha(active ? 0.78 : 0.16).setScale(active ? 1 : 0.82);
      line.setAlpha(active ? 0.92 : 0.28).setScale(active ? 1 : 0.82);
    });
    this.#rays.forEach((ray) => ray.setAlpha(hasRewards ? 0.7 : 0.18));
    this.#vault.setY(Math.round(this.#layoutHeight * 0.62));

    if (this.#visualState.reducedMotion || !hasRewards) return;
    this.#tweens.push(
      this.tweens.add({
        targets: this.#vault,
        y: this.#vault.y - 4,
        duration: 2600,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      }),
    );
    this.#facets.forEach(({ body, line }, index) => {
      if (index >= Math.min(4, Math.max(1, this.#visualState.ledgerCount))) return;
      this.#tweens.push(
        this.tweens.add({
          targets: [body, line],
          alpha: index % 2 === 0 ? 0.46 : 0.58,
          duration: 1500 + index * 180,
          delay: index * 160,
          yoyo: true,
          repeat: -1,
          ease: "Sine.easeInOut",
        }),
      );
    });
    this.#rays.forEach((ray, index) => {
      this.#tweens.push(
        this.tweens.add({
          targets: ray,
          alpha: index % 2 === 0 ? 0.22 : 0.34,
          duration: 1900 + index * 90,
          delay: index * 120,
          yoyo: true,
          repeat: -1,
          ease: "Sine.easeInOut",
        }),
      );
    });
  }
}
